import React from 'react'
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import './fonts.css'

type ErrorBoundaryProps = { children: React.ReactNode }
type ErrorBoundaryState = { hasError: boolean; error?: Error }

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // You can send this to a logging endpoint if desired
    console.error('Kavithai ErrorBoundary caught an error', error, info)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
    // naive reset: reload the route to recover
    if (typeof window !== 'undefined') window.location.reload()
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      const message = this.state.error?.message || 'Unexpected error'
      return (
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, md: 6 },
            py: 8,
            position: 'relative',
            overflow: 'hidden',
            background: `
              radial-gradient(900px circle at 15% 20%, rgba(120, 175, 255, 0.18), transparent 55%),
              radial-gradient(1000px circle at 85% 0%, rgba(109, 214, 214, 0.16), transparent 60%),
              linear-gradient(120deg, #0b0d10 0%, #121621 55%, #0f1119 100%)
            `,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 420,
              height: 420,
              borderRadius: '50%',
              background: 'rgba(255, 203, 107, 0.15)',
              filter: 'blur(90px)',
              top: -160,
              right: -120,
            }}
          />
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              zIndex: 1,
              width: 'min(760px, 100%)',
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(18, 21, 30, 0.85)',
              backdropFilter: 'blur(18px)',
              boxShadow: '0 22px 60px rgba(0,0,0,0.45)',
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label="System Notice"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.7)',
                    fontFamily: '"Open Sans", "Source Sans Pro", sans-serif',
                  }}
                />
              </Stack>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: '"Noto Serif Tamil", "Noto Sans Tamil", serif',
                  fontWeight: 700,
                  background: 'linear-gradient(120deg, #8bb3ff 0%, #6dd6d6 100%)',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Etwas ist schiefgelaufen
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(231, 236, 243, 0.7)',
                  fontFamily: '"Open Sans", "Source Sans Pro", sans-serif',
                }}
              >
                Bitte lade die Seite neu. Wenn das Problem bestehen bleibt, melde dich gern.
              </Typography>

              <Box component="details" sx={{ color: 'rgba(231,236,243,0.65)' }}>
                <summary style={{ cursor: 'pointer', marginBottom: 8 }}>
                  Fehlerdetails anzeigen
                </summary>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    fontFamily: '"Source Code Pro", "Courier New", monospace',
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {message}
                </Box>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={this.handleReset}
                  sx={{
                    background: 'linear-gradient(120deg, #7aa7ff 0%, #6dd6d6 100%)',
                    color: '#0b0d10',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    px: 3,
                  }}
                >
                  Neu laden
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HomeRoundedIcon />}
                  onClick={() => (window.location.href = '/')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'rgba(255,255,255,0.85)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  Zur Startseite
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      )
    }
    return this.props.children
  }
}
